import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { LeaveCalendar } from '@gokrew/ui/components/leave-calendar';
import type { CalendarEvent } from '@gokrew/ui/types/leave-calendar';
import { TranslationNS } from '@gokrew/models/constants';
import type { MyTeamApprovalRequest } from '@gokrew/models/my-team';
import type { LeaveDecisionContext } from '@gokrew/models/leave-management';
import {
  useMyTeamAccess,
  useMyTeamApprovals,
  useMyTeamCalendar,
} from '@/hooks/my-team/useMyTeam';
import { useLeaveSettings } from '@/hooks/leave-management';
import { CardLoader } from '@/pages/Dashboard/CardLoader';
import styles from './ApprovalCalendar.module.css';

/** Real, server-scoped team absences around the request, not just overlaps. */
export const ApprovalCalendar = ({
  request,
  context,
}: {
  request: MyTeamApprovalRequest;
  context?: LeaveDecisionContext;
}) => {
  const { t } = useTranslation(TranslationNS.MyTeam);
  const [month, setMonth] = useState(() => ({
    year: dayjs(request.startDate).year(),
    month: dayjs(request.startDate).month(),
  }));
  const from = dayjs(new Date(month.year, month.month, 1))
    .startOf('week')
    .format('YYYY-MM-DD');
  const to = dayjs(new Date(month.year, month.month + 1, 0))
    .endOf('week')
    .format('YYYY-MM-DD');
  const { data: access } = useMyTeamAccess();
  const manager = !!access?.enabled && !!access.isManager;
  const calendar = useMyTeamCalendar({ startDate: from, endDate: to });
  const pending = useMyTeamApprovals();
  const { data: settings } = useLeaveSettings();
  const nonWorking = useMemo(
    () => settings?.data.leaveSetting.weeklyHolidays?.map(Number) ?? [],
    [settings]
  );
  const events = useMemo(() => {
    const entries: CalendarEvent[] = [];
    for (const [date, leaves] of Object.entries(
      calendar.data?.data.leaves ?? {}
    )) {
      for (const leave of leaves) {
        entries.push({
          id: `${leave.leaveRequestId}-${date}`,
          start: date,
          extendedProps: {
            type: 'leave',
            label:
              `${leave.employee.firstName} ${leave.employee.lastName ?? ''}`.trim(),
            className: styles.approved,
            leave: {
              ...leave,
              reason: '',
              startSession: leave.startSession ?? undefined,
              endSession: leave.endSession ?? undefined,
              startTime: leave.startTime ?? undefined,
              endTime: leave.endTime ?? undefined,
              employee: {
                ...leave.employee,
                lastName: leave.employee.lastName ?? '',
                email: '',
              },
            },
            leaveRange: { start: leave.startDate, end: leave.endDate },
          },
        });
      }
    }
    // Delegates have no roster access. Their context is restricted to this
    // request's overlap dates; never claim it is a complete team calendar.
    if (!manager) {
      for (const leave of context?.teamAvailability ?? []) {
        for (
          let day = dayjs(leave.startDate);
          !day.isAfter(dayjs(leave.endDate));
          day = day.add(1, 'day')
        ) {
          entries.push({
            id: `context-${leave.employeeId}-${day.format('YYYY-MM-DD')}`,
            start: day.format('YYYY-MM-DD'),
            extendedProps: {
              type: 'leave',
              label: leave.name,
              className:
                leave.status === 'approved' ? styles.approved : styles.pending,
            },
          });
        }
      }
    }
    for (const leave of [
      request,
      ...(pending.data ?? []).filter((item) => item.id !== request.id),
    ]) {
      for (
        let day = dayjs(leave.startDate > from ? leave.startDate : from);
        !day.isAfter(dayjs(leave.endDate < to ? leave.endDate : to));
        day = day.add(1, 'day')
      ) {
        if (nonWorking.includes(day.day())) continue;
        const selected = leave.id === request.id;
        entries.push({
          id: `${leave.id}-${day.format('YYYY-MM-DD')}`,
          start: day.format('YYYY-MM-DD'),
          extendedProps: {
            type: 'leave',
            label: `${leave.employeeName} (${t(selected ? 'workspace.calendar.requested' : 'workspace.calendar.pending')})`,
            className: selected ? styles.selected : styles.pending,
          },
        });
      }
    }
    for (const holiday of calendar.data?.data.holidays ?? [])
      entries.push({
        id: `holiday-${holiday.date}`,
        start: holiday.date,
        extendedProps: { type: 'holiday', label: holiday.name },
      });
    return entries;
  }, [
    calendar.data,
    pending.data,
    context,
    request,
    from,
    to,
    manager,
    nonWorking,
    t,
  ]);
  return (
    <section
      className={styles.section}
      aria-label={t('workspace.calendar.title')}
    >
      <div className={styles.header}>
        <h3>{t('workspace.calendar.title')}</h3>
        <button
          type="button"
          onClick={() =>
            setMonth({
              year: dayjs(request.startDate).year(),
              month: dayjs(request.startDate).month(),
            })
          }
        >
          {t('workspace.calendar.return')}
        </button>
      </div>
      {!manager && (
        <p className={styles.hint}>{t('workspace.calendar.delegate_scope')}</p>
      )}
      {calendar.isLoading && <CardLoader />}
      {manager && calendar.isError && (
        <p role="alert" className={styles.error}>
          {t('workspace.calendar.error')}
        </p>
      )}
      <LeaveCalendar
        className={styles.calendar}
        selectedYear={month.year}
        selectedMonth={month.month}
        events={events}
        nonWorkingDays={nonWorking}
        legends={[
          {
            label: t('workspace.calendar.requested'),
            className: styles.selected,
          },
          {
            label: t('workspace.calendar.approved'),
            className: styles.approved,
          },
          { label: t('workspace.calendar.pending'), className: styles.pending },
        ]}
        onDateChange={(next) =>
          setMonth((previous) =>
            previous.month === next.month && previous.year === next.year
              ? previous
              : { month: next.month, year: next.year }
          )
        }
      />
    </section>
  );
};
